import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Button, Card, Field, Input, LoadingScreen, ProgressBar } from './primitives';
import { useToast } from './overlays';

/**
 * Écrans hors session, partagés par les deux applications.
 *
 * Ces trois écrans étaient dupliqués au caractère près : 393 lignes en double,
 * pour trois différences de fond (le public visé, la phrase de sous-titre, le
 * nom de l'autre application). C'est cette duplication qui a produit le défaut
 * le plus visible du projet — l'écran de connexion Parents annonçait aux
 * familles qu'elles n'étaient pas au bon endroit, parce que le fichier avait
 * été copié depuis Staff sans en réécrire le pied de page.
 *
 * Le module reste sans dépendance à React Router ni au client HTTP des
 * applications : ce qui vient de l'extérieur est passé en propriété. C'est ce
 * qui permet à `packages/ui` de ne dépendre que de React.
 */

// ---------------------------------------------------------------------------
// Cadre
// ---------------------------------------------------------------------------

export function AuthLayout({
  title,
  subtitle,
  children,
  crestSize = 132,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  /**
   * Côté Parents le blason était dessiné à 124 px, sans raison écrite — seule
   * divergence des 66 lignes. La propriété existe pour que le choix reste
   * possible, mais les deux applications prennent aujourd'hui la même valeur.
   */
  crestSize?: number;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100dvh',
        padding: 'var(--s-4)',
        background:
          'radial-gradient(1200px 600px at 50% -10%, var(--accent-soft), transparent 60%), var(--bg)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          animation: 'bala-rise var(--d-slow) var(--ease-out)',
        }}
      >
        <header style={{ textAlign: 'center', marginBottom: 'var(--s-6)' }}>
          {/* Grand format : c'est l'écran où l'emblème est lisible en entier,
              et il dit à qui appartient l'application avant même le titre. */}
          <img
            src="/emblem.webp"
            alt="Emblème de la 11e Unité Saint-Louis Bastogne"
            width={crestSize}
            height={crestSize}
            style={{
              width: crestSize,
              height: crestSize,
              margin: '0 auto var(--s-4)',
              objectFit: 'contain',
            }}
          />
          <span className="bala-eyebrow" style={{ marginBottom: 'var(--s-2)' }}>
            11<sup style={{ fontSize: '0.7em' }}>e</sup> Unité Saint-Louis Bastogne
          </span>
          <h1 style={{ font: 'var(--t-title-1)', letterSpacing: '-0.01em' }}>{title}</h1>
          {subtitle && (
            <p style={{ font: 'var(--t-subhead)', color: 'var(--text-secondary)', marginTop: 'var(--s-2)' }}>
              {subtitle}
            </p>
          )}
          <div className="bala-rope" style={{ width: 72, margin: 'var(--s-4) auto 0' }} />
        </header>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Connexion
// ---------------------------------------------------------------------------

export interface LoginScreenProps {
  /** Ce que l'application permet de faire, en une phrase. */
  subtitle: string;
  /**
   * Pied de page : à qui s'adresse cette application, et où vont les autres.
   * Passé en propriété précisément parce que c'est la ligne que la copie du
   * fichier avait laissée fausse pendant toute la vie du projet.
   */
  audienceNote: ReactNode;
  /**
   * Renvoie un jeton de défi quand le compte demande un second facteur : le mot
   * de passe est alors validé, mais aucune session n'est ouverte.
   */
  onSignIn: (email: string, password: string) => Promise<{ challenge?: string } | void>;
  onForgot: (email: string) => Promise<void>;
  /**
   * Second temps de la connexion. Absent côté familles, qui n'ont pas de double
   * authentification : l'écran de code n'y apparaît alors jamais.
   */
  onVerifyCode?: (challenge: string, code: string) => Promise<void>;
}

export function LoginScreen({
  subtitle,
  audienceNote,
  onSignIn,
  onForgot,
  onVerifyCode,
}: LoginScreenProps) {
  const toast = useToast();
  const [mode, setMode] = useState<'login' | 'forgot' | 'code'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Second facteur : le défi rendu par la première étape, et le code saisi.
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);

  const submitLogin = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await onSignIn(email, password);
      if (result && result.challenge && onVerifyCode) {
        setChallenge(result.challenge);
        setCode('');
        setUseBackup(false);
        setMode('code');
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Connexion impossible.');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (event: FormEvent) => {
    event.preventDefault();
    if (!challenge || !onVerifyCode) return;
    setBusy(true);
    setError(null);
    try {
      await onVerifyCode(challenge, code);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Code refusé.');
      // Un défi expiré ne se rattrape pas : on repart du mot de passe plutôt
      // que de laisser saisir des codes dans le vide.
      if (caught instanceof Error && /expirée/i.test(caught.message)) {
        setChallenge(null);
        setMode('login');
      }
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onForgot(email);
      // Le serveur répond toujours la même chose : on n'indique jamais si
      // l'adresse existe.
      toast.success('Si un compte existe pour cette adresse, un email vient d’être envoyé.');
      setMode('login');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Demande impossible.');
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'code') {
    return (
      <AuthLayout
        title="Vérification"
        subtitle={
          useBackup
            ? 'Saisissez l’un des codes de secours notés lors de l’activation.'
            : 'Ouvrez votre application d’authentification et recopiez le code à six chiffres.'
        }
      >
        <Card padding="lg">
          <form onSubmit={submitCode} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}>
            <Field label={useBackup ? 'Code de secours' : 'Code à six chiffres'} htmlFor="mfa-code">
              <Input
                id="mfa-code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                // `one-time-code` permet à iOS et Android de proposer le code
                // directement au-dessus du clavier.
                autoComplete="one-time-code"
                inputMode={useBackup ? 'text' : 'numeric'}
                autoCapitalize={useBackup ? 'characters' : 'none'}
                spellCheck={false}
                autoFocus
                required
                placeholder={useBackup ? 'XXXXX-XXXXX' : '000000'}
              />
            </Field>

            {error && (
              <p
                role="alert"
                style={{
                  font: 'var(--t-footnote)',
                  color: 'var(--red)',
                  background: 'var(--red-soft)',
                  padding: 'var(--s-3)',
                  borderRadius: 'var(--r-md)',
                }}
              >
                {error}
              </p>
            )}

            <Button type="submit" variant="primary" size="lg" block loading={busy}>
              Valider
            </Button>

            <Button
              type="button"
              variant="plain"
              size="sm"
              onClick={() => {
                setUseBackup(!useBackup);
                setCode('');
                setError(null);
              }}
            >
              {useBackup ? 'Utiliser mon application d’authentification' : 'J’ai perdu mon téléphone'}
            </Button>

            <Button
              type="button"
              variant="plain"
              size="sm"
              onClick={() => {
                setMode('login');
                setChallenge(null);
                setError(null);
              }}
            >
              Revenir à la connexion
            </Button>
          </form>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={mode === 'login' ? 'Bala’App' : 'Mot de passe oublié'}
      subtitle={
        mode === 'login'
          ? subtitle
          : 'Indiquez votre adresse : vous recevrez un lien pour choisir un nouveau mot de passe.'
      }
    >
      <Card padding="lg">
        <form
          onSubmit={mode === 'login' ? submitLogin : submitForgot}
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}
        >
          <Field label="Adresse email" htmlFor="email">
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              required
              placeholder="prenom.nom@exemple.be"
            />
          </Field>

          {mode === 'login' && (
            <Field label="Mot de passe" htmlFor="password">
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
            </Field>
          )}

          {error && (
            <p
              role="alert"
              style={{
                font: 'var(--t-footnote)',
                color: 'var(--red)',
                background: 'var(--red-soft)',
                padding: 'var(--s-3)',
                borderRadius: 'var(--r-md)',
              }}
            >
              {error}
            </p>
          )}

          <Button type="submit" variant="primary" size="lg" block loading={busy}>
            {mode === 'login' ? 'Se connecter' : 'Envoyer le lien'}
          </Button>

          <Button
            type="button"
            variant="plain"
            size="sm"
            onClick={() => {
              setMode(mode === 'login' ? 'forgot' : 'login');
              setError(null);
            }}
          >
            {mode === 'login' ? 'Mot de passe oublié ?' : 'Revenir à la connexion'}
          </Button>
        </form>
      </Card>

      <p style={{ font: 'var(--t-caption)', color: 'var(--text-tertiary)', textAlign: 'center', marginTop: 'var(--s-4)' }}>
        {audienceNote}
      </p>
    </AuthLayout>
  );
}

// ---------------------------------------------------------------------------
// Invitation et réinitialisation
// ---------------------------------------------------------------------------

export interface TokenInfo {
  purpose: 'invitation' | 'password_reset';
  audienceType: 'user' | 'parent';
  email: string;
  firstName: string;
  lastName: string;
}

/**
 * Ce dont l'écran a besoin de l'application hôte.
 *
 * Un objet plutôt qu'une poignée de fonctions éparses : les trois opérations
 * forment une seule séquence (vérifier le lien, l'accepter, ouvrir la session),
 * et les séparer inviterait à n'en brancher que deux.
 */
export interface TokenClient {
  lookup: (token: string) => Promise<TokenInfo>;
  accept: (token: string, password: string) => Promise<{ audienceType: string }>;
  /** La session est déjà ouverte côté serveur : il ne reste qu'à charger l'identité. */
  openSession: () => Promise<void>;
}

export interface TokenScreenProps {
  /** Le jeton, lu par l'application dans l'URL — d'où l'absence de routeur ici. */
  token: string;
  /** Public attendu par cette application. */
  audience: 'user' | 'parent';
  client: TokenClient;
}

/** « animateur »/« parent », et le nom de l'application où le lien serait valide. */
const AUDIENCE_COPY = {
  user: { noun: 'animateur', otherNoun: 'parent', otherApp: 'Bala’App Parents' },
  parent: { noun: 'parent', otherNoun: 'animateur', otherApp: 'Bala’App Staff' },
} as const;

export function TokenScreen({ token, audience, client }: TokenScreenProps) {
  const copy = AUDIENCE_COPY[audience];
  // Un lien destiné à l'autre public n'est pas invalide : il est au mauvais
  // endroit. Le message le dit, plutôt que de laisser croire à une expiration.
  const wrongAudience = `Ce lien concerne un compte ${copy.otherNoun} : ouvrez-le dans l'application ${copy.otherApp}.`;

  const [info, setInfo] = useState<TokenInfo | null>(null);
  const [checking, setChecking] = useState(true);
  const [invalid, setInvalid] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    client
      .lookup(token)
      .then((result) => {
        if (!active) return;
        if (result.audienceType !== audience) {
          setInvalid(wrongAudience);
          return;
        }
        setInfo(result);
      })
      .catch((caught: unknown) => {
        if (active) setInvalid(caught instanceof Error ? caught.message : 'Ce lien est invalide.');
      })
      .finally(() => {
        if (active) setChecking(false);
      });
    return () => {
      active = false;
    };
    // `client` est recréé à chaque rendu de l'appelant : le suivre relancerait
    // la vérification en boucle. Le jeton est la seule entrée qui compte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, audience]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password !== confirm) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const session = await client.accept(token, password);
      if (session.audienceType !== audience) {
        setError(wrongAudience);
        return;
      }
      await client.openSession();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Impossible de définir le mot de passe.');
    } finally {
      setBusy(false);
    }
  };

  if (checking) return <LoadingScreen message="Vérification du lien…" />;

  if (invalid || !info) {
    return (
      <AuthLayout title="Lien expiré" subtitle={invalid ?? 'Ce lien n’est plus valable.'}>
        <Card padding="lg">
          <p style={{ font: 'var(--t-subhead)', color: 'var(--text-secondary)' }}>
            Les liens d’invitation sont valables 14 jours, ceux de réinitialisation 2 heures. Demandez
            à un Baygon de vous renvoyer une invitation, ou refaites une demande de mot de passe oublié.
          </p>
          <Button variant="secondary" block style={{ marginTop: 'var(--s-4)' }} onClick={() => window.location.assign('/')}>
            Revenir à la connexion
          </Button>
        </Card>
      </AuthLayout>
    );
  }

  const isInvitation = info.purpose === 'invitation';
  const strength = passwordStrength(password);

  // Les mêmes règles que le serveur, appliquées ici pour que l'utilisateur les
  // voie en écrivant plutôt qu'après avoir validé. C'est l'écran de changement
  // de mot de passe des réglages qui donne le modèle.
  const tooShort = password.length > 0 && password.length < 8;
  const missingLetter = password.length > 0 && !/[a-zA-Z]/.test(password);
  const missingDigit = password.length > 0 && !/\d/.test(password);
  const ruleError = tooShort
    ? 'Au moins 8 caractères.'
    : missingLetter
      ? 'Il faut au moins une lettre.'
      : missingDigit
        ? 'Il faut au moins un chiffre.'
        : undefined;
  const ready = password.length >= 8 && !missingLetter && !missingDigit && confirm === password;

  return (
    <AuthLayout
      title={isInvitation ? `Bienvenue, ${info.firstName} !` : 'Nouveau mot de passe'}
      subtitle={
        isInvitation
          ? `Choisissez un mot de passe pour activer votre compte ${copy.noun}.`
          : `Choisissez un nouveau mot de passe pour ${info.email}.`
      }
    >
      <Card padding="lg">
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}>
          <Field
            label="Mot de passe"
            htmlFor="password"
            hint="Au moins 8 caractères, avec une lettre et un chiffre."
            error={ruleError}
          >
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              autoFocus
              required
            />
          </Field>

          {password.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-1)' }}>
              <ProgressBar value={strength.score} max={4} tone={strength.tone} height={5} />
              <span style={{ font: 'var(--t-caption)', color: 'var(--text-secondary)' }}>{strength.label}</span>
            </div>
          )}

          <Field
            label="Confirmation"
            htmlFor="confirm"
            error={confirm.length > 0 && confirm !== password ? 'Les mots de passe diffèrent.' : undefined}
          >
            <Input
              id="confirm"
              type="password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              autoComplete="new-password"
              required
            />
          </Field>

          {error && (
            <p
              role="alert"
              style={{
                font: 'var(--t-footnote)',
                color: 'var(--red)',
                background: 'var(--red-soft)',
                padding: 'var(--s-3)',
                borderRadius: 'var(--r-md)',
              }}
            >
              {error}
            </p>
          )}

          <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={!ready}>
            {isInvitation ? 'Activer mon compte' : 'Enregistrer'}
          </Button>
        </form>
      </Card>
    </AuthLayout>
  );
}

/**
 * Robustesse au-delà du minimum exigé. Le minimum, lui, est bloquant des deux
 * côtés : ici pour le dire tout de suite, et sur le serveur parce que c'est le
 * seul endroit qui compte vraiment. S'y ajoute côté serveur le refus des mots
 * de passe déjà présents dans des fuites publiques — une composition correcte ne
 * dit rien de la qualité réelle du mot de passe.
 */
function passwordStrength(password: string): { score: number; label: string; tone: 'red' | 'orange' | 'green' } {
  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password) && /[^\w\s]/.test(password)) score += 1;

  if (score <= 1) return { score: Math.max(1, score), label: 'Mot de passe faible', tone: 'red' };
  if (score === 2) return { score, label: 'Mot de passe correct', tone: 'orange' };
  if (score === 3) return { score, label: 'Bon mot de passe', tone: 'green' };
  return { score: 4, label: 'Excellent mot de passe', tone: 'green' };
}
